/* ──────────────────────────────────────────────────────────────────────
 * Patchistry — Reveal-on-scroll system
 * Added 2026-05-20 (sensory polish phase, Week 2).
 *
 * Single shared IntersectionObserver. Elements with [data-p-reveal] or
 * matching the auto-selector fade up as they enter the viewport.
 *
 * Auto-targets (no markup needed):
 *   - h1, h2, h3 within main content
 *   - p.pcr-eyebrow, p.pabout__label, p.pcr-intro-film__line
 *   - .pabout__body, .pabout__hero-sub
 *   - section-level CTAs (.button, .pabout__cta-btn, .pcr-canvas__next)
 *   - Cards: .patch-card, .canvas-card, .pcr-canvas-card
 *
 * Opt-in markup:
 *   <element data-p-reveal>          → default fade-up 600ms
 *   <element data-p-reveal="slow">   → slower 800ms
 *   <element data-p-reveal="fast">   → faster 300ms
 *   <element data-p-reveal-delay="80">  → 80ms stagger
 *
 * Constraints:
 *   - Skipped if prefers-reduced-motion
 *   - Skipped if element is already visible on first render (above the fold)
 *     — these get an immediate "visible" state so no flash
 *   - Idempotent — runs once per element via [data-p-revealed]
 * ─────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  // Bail on reduced motion
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.documentElement.classList.add('p-reveal-disabled');
    return;
  }

  // Bail if no IntersectionObserver (very old browsers)
  if (!('IntersectionObserver' in window)) {
    document.documentElement.classList.add('p-reveal-disabled');
    return;
  }

  // ── Inject base styles ─────────────────────────────────────────────
  var css =
    /* default hidden state */
    '[data-p-reveal]:not([data-p-revealed]){' +
      'opacity:0;' +
      'transform:translateY(16px);' +
      'transition:opacity 600ms cubic-bezier(0.16,1,0.3,1),' +
                 'transform 600ms cubic-bezier(0.16,1,0.3,1);' +
      'will-change:opacity,transform;' +
    '}' +
    /* slow / fast variants */
    '[data-p-reveal="slow"]:not([data-p-revealed]){' +
      'transition-duration:800ms;' +
      'transform:translateY(20px);' +
    '}' +
    '[data-p-reveal="fast"]:not([data-p-revealed]){' +
      'transition-duration:300ms;' +
      'transform:translateY(8px);' +
    '}' +
    /* revealed state */
    '[data-p-reveal][data-p-revealed]{' +
      'opacity:1!important;' +
      'transform:translateY(0)!important;' +
    '}' +
    /* safety: when disabled, no animation */
    '.p-reveal-disabled [data-p-reveal]{opacity:1!important;transform:none!important}';

  var styleEl = document.createElement('style');
  styleEl.id = 'patchistry-reveal-styles';
  styleEl.textContent = css;
  document.head.appendChild(styleEl);

  // ── Auto-target selector ───────────────────────────────────────────
  // Tag elements that should auto-reveal without explicit markup.
  // Keep this list tight — over-revealing makes the site feel busy.
  var AUTO_SELECTOR = [
    // Headings inside main page content (skip header / footer / drawers)
    'main h1', 'main h2',
    // Eyebrows + labels (premium typography moments)
    '.pcr-eyebrow', '.pabout__label', '.pabout__eyebrow',
    '.pcr-intro-film__line',
    '.p-order-celebrate__eyebrow',
    // Body / subtitle copy
    '.pabout__hero-sub', '.pabout__body',
    '.pcr-subtitle',
    // Founder marks (always entrance)
    '.p-founder-mark',
    // Section CTAs
    '.pabout__cta-btn', '.pabout__craft-link', '.pabout__text-link'
  ].join(',');

  // Apply data-p-reveal to auto-targets
  function tagAutoTargets(root) {
    var nodes;
    try {
      nodes = (root || document).querySelectorAll(AUTO_SELECTOR);
    } catch (e) { return; }
    nodes.forEach(function (el) {
      if (!el.hasAttribute('data-p-reveal')) el.setAttribute('data-p-reveal', '');
    });
  }

  // ── Mark above-the-fold elements as already revealed (no flash) ────
  function preRevealAboveFold() {
    var foldY = window.innerHeight * 0.85;
    document.querySelectorAll('[data-p-reveal]:not([data-p-revealed])').forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.top < foldY) {
        el.setAttribute('data-p-revealed', '');
      }
    });
  }

  // ── IntersectionObserver ───────────────────────────────────────────
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var el = entry.target;
      var delay = parseInt(el.getAttribute('data-p-reveal-delay'), 10) || 0;
      if (delay > 0) {
        setTimeout(function () { el.setAttribute('data-p-revealed', ''); }, delay);
      } else {
        el.setAttribute('data-p-revealed', '');
      }
      io.unobserve(el);
    });
  }, {
    root: null,
    rootMargin: '0px 0px -10% 0px',  // trigger slightly before fully in view
    threshold: 0.15
  });

  function observeAll(root) {
    var nodes;
    try {
      nodes = (root || document).querySelectorAll('[data-p-reveal]:not([data-p-revealed])');
    } catch (e) { return; }
    nodes.forEach(function (el) { io.observe(el); });
  }

  // ── Boot ───────────────────────────────────────────────────────────
  function boot() {
    tagAutoTargets(document);
    preRevealAboveFold();
    observeAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-scan after AJAX-loaded content (Build Yours filter changes, etc.)
  // Exposed as public API.
  window.Patchistry = window.Patchistry || {};
  window.Patchistry.Reveal = {
    rescan: function (root) {
      tagAutoTargets(root);
      observeAll(root);
    }
  };
})();
